/**
 * The formatting the Manpower screens share.
 *
 * Each of these existed two or three times over, once per screen: `formatHours` as
 * a named helper on Capacity & Utilisation and as an inline
 * `Math.round(…).toLocaleString()` on Demand Explorer and Capacity Inputs, and the
 * planning-year vocabulary as a `DEFAULT_PLANNING_YEAR` constant in two files. Four
 * screens reporting the same figures have to round them the same way, so the rule
 * lives once.
 *
 * No user-facing text here: the unit wrappers (`{{value}}h`, `{{value}}%`) are
 * catalogue keys the callers resolve (R18.9). These functions return bare numerals.
 */

/**
 * Hours reported whole.
 *
 * A fractional hour is below the precision of the inputs it is derived from —
 * headcount, whole workdays and a two-decimal factor — so printing one would imply
 * accuracy the calculation does not have. Localised so a thousands separator
 * follows the reader's locale.
 */
export function formatHours(hours: number): string {
  return Math.round(hours).toLocaleString();
}

/**
 * The planning year a screen defaults to.
 *
 * A function rather than a module constant: a constant is evaluated once when the
 * module is first imported, which strands a long-lived tab on the previous year
 * after New Year — the same reason `router.tsx` resolves the calendar's year inside
 * the redirect component rather than while building the route table.
 */
export function currentPlanningYear(): number {
  return new Date().getFullYear();
}

/** How many years ahead of the current one the year selectors offer. */
const PLANNING_YEAR_HORIZON = 4;

/**
 * The years a planning-year selector offers: the current one and four ahead.
 *
 * Forward-only. Manpower figures are a plan, and the screens that read them are
 * for deciding next year's capacity, not for restating a closed one.
 */
export function planningYearOptions(): readonly number[] {
  const base = currentPlanningYear();
  return Array.from({ length: PLANNING_YEAR_HORIZON + 1 }, (_, offset) => base + offset);
}
